import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, TrendingDown, Minus, Trophy, Dumbbell, Apple,
  Moon, Calendar, Clock, Flame, Target, Award, ChevronRight,
  CheckCircle, XCircle, Star, Zap, Heart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, subDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay } from 'date-fns';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar } from 'recharts';

const WeeklySummaryPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [weekData, setWeekData] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchWeeklyData();
  }, []);

  const fetchWeeklyData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, workoutsRes, recoveryRes, nutritionRes, badgesRes, profileRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/workouts`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/recovery`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/nutrition`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/badges`, { headers }).catch(() => ({ data: { badges: [], total_points: 0 } })),
        axios.get(`${API}/athlete/profile`, { headers }).catch(() => ({ data: {} }))
      ]);

      // Process data for the week
      const weekStart = startOfWeek(new Date());
      const weekEnd = endOfWeek(new Date());
      const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

      const workouts = Array.isArray(workoutsRes.data) ? workoutsRes.data : [];
      const recoveryLogs = Array.isArray(recoveryRes.data) ? recoveryRes.data : [];
      const nutritionLogs = Array.isArray(nutritionRes.data) ? nutritionRes.data : [];

      // Filter for this week
      const weekWorkouts = workouts.filter(w => {
        const date = new Date(w.created_at);
        return date >= weekStart && date <= weekEnd;
      });

      const weekRecovery = recoveryLogs.filter(r => {
        const date = new Date(r.created_at);
        return date >= weekStart && date <= weekEnd;
      });

      // Calculate daily data
      const dailyData = days.map(day => {
        const dayWorkouts = weekWorkouts.filter(w => isSameDay(new Date(w.created_at), day));
        const dayRecovery = weekRecovery.find(r => isSameDay(new Date(r.created_at), day));
        
        return {
          day: format(day, 'EEE'),
          date: day,
          workouts: dayWorkouts.length,
          minutes: dayWorkouts.reduce((sum, w) => sum + (w.duration_minutes || 0), 0),
          recovery: dayRecovery?.recovery_score || null,
          sleep: dayRecovery?.sleep_hours || null
        };
      });

      // Calculate totals
      const totalWorkouts = weekWorkouts.length;
      const totalMinutes = weekWorkouts.reduce((sum, w) => sum + (w.duration_minutes || 0), 0);
      const avgRecovery = weekRecovery.length > 0 
        ? Math.round(weekRecovery.reduce((sum, r) => sum + r.recovery_score, 0) / weekRecovery.length)
        : null;
      const avgSleep = weekRecovery.length > 0
        ? (weekRecovery.reduce((sum, r) => sum + (r.sleep_hours || 0), 0) / weekRecovery.length).toFixed(1)
        : null;

      // Get earned badges this week
      const earnedBadges = (badgesRes.data.badges || []).filter(b => {
        if (!b.earned || !b.earned_at) return false;
        const earnedDate = new Date(b.earned_at);
        return earnedDate >= weekStart && earnedDate <= weekEnd;
      });

      setWeekData({
        dailyData,
        totalWorkouts,
        totalMinutes,
        avgRecovery,
        avgSleep,
        earnedBadges,
        auraScore: badgesRes.data.total_points || 0,
        stats: statsRes.data
      });
      setProfile(profileRes.data);
    } catch (error) {
      console.error('Failed to fetch weekly data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getTrend = (current, target) => {
    if (current >= target) return { icon: TrendingUp, color: 'text-green-400', label: 'On Track' };
    if (current >= target * 0.7) return { icon: Minus, color: 'text-yellow-400', label: 'Improving' };
    return { icon: TrendingDown, color: 'text-red-400', label: 'Needs Focus' };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-aura-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const workoutTrend = getTrend(weekData?.totalWorkouts || 0, 5);
  const completedGoals = (profile?.shortTermGoals?.filter(g => g.completed)?.length || 0) + 
                        (profile?.longTermGoals?.filter(g => g.completed)?.length || 0);
  const totalGoals = (profile?.shortTermGoals?.length || 0) + (profile?.longTermGoals?.length || 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              Performance Progression
            </h1>
            <p className="text-aura-text-muted mt-1">
              Track how you are developing over time.
            </p>
          </div>
          <Badge className="bg-aura-primary/20 text-aura-primary border-0 text-lg px-4 py-2">
            <Trophy className="w-5 h-5 mr-2" />
            {weekData?.auraScore || 0} Aura Performance Score
          </Badge>
        </div>

        {/* Key Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-aura-primary/20 border-0">
            <CardContent className="p-4 text-center">
              <Dumbbell className="w-8 h-8 text-aura-primary mx-auto mb-2" />
              <p className="font-heading font-bold text-4xl text-aura-primary">
                {weekData?.totalWorkouts || 0}
              </p>
              <p className="text-sm text-aura-text-muted">Sessions</p>
              <div className={`flex items-center justify-center gap-1 mt-2 ${workoutTrend.color}`}>
                <workoutTrend.icon className="w-4 h-4" />
                <span className="text-xs">{workoutTrend.label}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-orange-500/20 border-0">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 text-orange-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-4xl text-orange-400">
                {weekData?.totalMinutes || 0}
              </p>
              <p className="text-sm text-aura-text-muted">Minutes</p>
              <p className="text-xs text-aura-text-subtle mt-2">
                ~{Math.round((weekData?.totalMinutes || 0) / 7)} min/day
              </p>
            </CardContent>
          </Card>

          <Card className="bg-cyan-500/20 border-0">
            <CardContent className="p-4 text-center">
              <Moon className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
              <p className={`font-heading font-bold text-4xl ${getScoreColor(weekData?.avgRecovery || 0)}`}>
                {weekData?.avgRecovery || '--'}
              </p>
              <p className="text-sm text-aura-text-muted">Avg Recovery</p>
              <p className="text-xs text-aura-text-subtle mt-2">
                {weekData?.avgSleep || '--'}h avg sleep
              </p>
            </CardContent>
          </Card>

          <Card className="bg-yellow-500/20 border-0">
            <CardContent className="p-4 text-center">
              <Target className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-4xl text-yellow-400">
                {completedGoals}/{totalGoals}
              </p>
              <p className="text-sm text-aura-text-muted">Goals Done</p>
              {totalGoals > 0 && (
                <Progress 
                  value={(completedGoals / totalGoals) * 100} 
                  className="h-1 mt-2" 
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Activity Chart */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Performance Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekData?.dailyData || []}>
                  <XAxis dataKey="day" stroke="#71717A" fontSize={12} />
                  <YAxis stroke="#71717A" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181B', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="minutes" fill="#F97316" radius={[4, 4, 0, 0]} name="Minutes" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recovery Trend */}
        {weekData?.dailyData?.some(d => d.recovery) && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader>
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                Recovery Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={weekData?.dailyData || []}>
                    <defs>
                      <linearGradient id="recoveryGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#06B6D4" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="day" stroke="#71717A" fontSize={12} />
                    <YAxis stroke="#71717A" fontSize={12} domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181B', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="recovery" 
                      stroke="#06B6D4" 
                      strokeWidth={2}
                      fill="url(#recoveryGrad)" 
                      name="Recovery Score"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Badges Earned This Week */}
        {weekData?.earnedBadges?.length > 0 && (
          <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30">
            <CardHeader>
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-400" />
                Badges Earned This Week
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {weekData.earnedBadges.map((badge, idx) => {
                  const tierColors = {
                    bronze: 'bg-amber-600',
                    silver: 'bg-slate-400',
                    gold: 'bg-yellow-400'
                  };
                  return (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <div className={`w-12 h-12 rounded-full ${tierColors[badge.tier] || 'bg-aura-primary'} flex items-center justify-center`}>
                        <Trophy className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{badge.name}</p>
                        <p className="text-xs text-aura-text-muted capitalize">{badge.tier} • +{badge.points} pts</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Why Statement Reminder */}
        {profile?.whyStatement && (
          <Card className="bg-gradient-to-r from-aura-primary/20 to-aura-secondary/20 border-aura-primary/30">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-aura-primary rounded-xl flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">Keep Going - Remember Why You Started</p>
                  <p className="text-white italic text-lg">"{profile.whyStatement}"</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Call to Action */}
        <div className="flex flex-wrap gap-4 justify-center">
          <Link to="/training-program">
            <Button className="bg-aura-primary hover:bg-aura-primary-hover text-white">
              <Dumbbell className="w-4 h-4 mr-2" />
              Execute Session
            </Button>
          </Link>
          <Link to="/my-profile">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <Target className="w-4 h-4 mr-2" />
              Update Goals
            </Button>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WeeklySummaryPage;
